import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Sun, Moon, Globe, ChevronDown } from 'lucide-react';
import { Language, ThemeMode, SiteBranding } from '../types';
import { translations } from '../i18n/translations';
import { BrandLogo } from './BrandLogo';
import { getSiteBranding } from '../services/siteSettingsStorage';

interface HeaderProps {
  currentLang: Language;
  onLanguageChange: (lang: Language) => void;
  activeSection: string;
  onNavigate: (sectionId: string) => void;
  isAdminLoggedIn: boolean;
  onOpenAdmin: () => void;
  theme: ThemeMode;
  onToggleTheme: (e?: React.MouseEvent) => void;
  siteBranding?: SiteBranding;
}

export const Header: React.FC<HeaderProps> = ({
  currentLang,
  onLanguageChange,
  onNavigate,
  theme,
  onToggleTheme,
  siteBranding: propBranding,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const [localBranding, setLocalBranding] = useState<SiteBranding>(() => propBranding || getSiteBranding());
  const t = translations[currentLang];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (propBranding) setLocalBranding(propBranding);
  }, [propBranding]);

  useEffect(() => {
    const handleBrandingChange = (e: Event) => {
      const customEvent = e as CustomEvent<SiteBranding>;
      if (customEvent.detail) setLocalBranding(customEvent.detail);
      else setLocalBranding(getSiteBranding());
    };
    window.addEventListener('chc_site_branding_updated', handleBrandingChange);
    return () => window.removeEventListener('chc_site_branding_updated', handleBrandingChange);
  }, []);

  const handleNav = (page: string) => {
    onNavigate(page);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800 transition-colors">
        <div className="w-[90%] max-w-none mx-auto px-6 h-16 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => handleNav('accueil')}>
            <BrandLogo 
              variant="compact" 
              customLogoUrl={localBranding.logoUrl} 
              customLogoUrlDark={localBranding.logoUrlDark}
              customCompanyName={localBranding.companyName}
            />
          </div>
          
          <div className="flex items-center gap-3">
            {/* Top Bar Language Selector */}
            <div className="relative" ref={langDropdownRef}>
              <button
                type="button"
                id="language-switcher-btn"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors rounded border border-slate-300 dark:border-slate-700 shadow-sm focus:outline-none"
                aria-label="Changer de langue / Change language / تغيير اللغة"
              >
                <Globe className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                <span className="font-bold tracking-wider">
                  {currentLang === 'ar' ? 'العربية' : currentLang === 'en' ? 'EN' : 'FR'}
                </span>
                <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {langMenuOpen && (
                <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-1.5 w-36 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded shadow-xl py-1 z-50">
                  <button
                    type="button"
                    onClick={() => { onLanguageChange('fr'); setLangMenuOpen(false); }}
                    className={`w-full text-left rtl:text-right px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${currentLang === 'fr' ? 'font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50/50 dark:bg-cyan-950/30' : 'text-slate-700 dark:text-slate-300'}`}
                  >
                    <span>Français</span>
                    <span className="text-[10px] text-slate-400 font-mono">FR</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { onLanguageChange('en'); setLangMenuOpen(false); }}
                    className={`w-full text-left rtl:text-right px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${currentLang === 'en' ? 'font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50/50 dark:bg-cyan-950/30' : 'text-slate-700 dark:text-slate-300'}`}
                  >
                    <span>English</span>
                    <span className="text-[10px] text-slate-400 font-mono">EN</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { onLanguageChange('ar'); setLangMenuOpen(false); }}
                    className={`w-full text-left rtl:text-right px-3 py-2 text-xs flex items-center justify-between hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors ${currentLang === 'ar' ? 'font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-50/50 dark:bg-cyan-950/30' : 'text-slate-700 dark:text-slate-300'}`}
                  >
                    <span>العربية</span>
                    <span className="text-[10px] text-slate-400 font-mono">AR</span>
                  </button>
                </div>
              )}
            </div>

            {/* Theme Toggle Button */}
            <button
              onClick={(e) => onToggleTheme(e)}
              className="p-2 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors rounded-sm focus:outline-none"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors rounded-sm focus:outline-none"
              aria-label="Menu"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Side Drawer Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex justify-end rtl:justify-start">
          <div className="absolute inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="relative w-full max-w-xs bg-white dark:bg-slate-900 h-full shadow-2xl flex flex-col animate-slideInRight">
            <div className="p-6 flex justify-end rtl:justify-start">
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex flex-col px-8 gap-6 mt-4">
              <button onClick={() => handleNav('expertises')} className="text-left rtl:text-right text-sm font-medium text-slate-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                {t.nav.services}
              </button>
              <button onClick={() => handleNav('formations')} className="text-left rtl:text-right text-sm font-medium text-slate-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                {t.nav.training}
              </button>
              <button onClick={() => handleNav('verifier')} className="text-left rtl:text-right text-sm font-medium text-slate-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                {t.nav.verify}
              </button>
              <button onClick={() => handleNav('apropos')} className="text-left rtl:text-right text-sm font-medium text-slate-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                {t.nav.about}
              </button>
              <button onClick={() => handleNav('contact')} className="text-left rtl:text-right text-sm font-medium text-slate-900 dark:text-white hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors">
                {t.nav.contact}
              </button>
            </nav>

            {/* Language Switcher in Drawer */}
            <div className="mt-auto pb-8 pt-6 border-t border-slate-200 dark:border-slate-800 px-8">
              <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                <span>{currentLang === 'ar' ? 'اللغة' : currentLang === 'en' ? 'Language' : 'Langue'}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => { onLanguageChange('fr'); setMobileMenuOpen(false); }}
                  className={`py-2 text-xs font-bold rounded border text-center transition-colors ${currentLang === 'fr' ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-sm' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  FR
                </button>
                <button
                  type="button"
                  onClick={() => { onLanguageChange('en'); setMobileMenuOpen(false); }}
                  className={`py-2 text-xs font-bold rounded border text-center transition-colors ${currentLang === 'en' ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-sm' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  EN
                </button>
                <button
                  type="button"
                  onClick={() => { onLanguageChange('ar'); setMobileMenuOpen(false); }}
                  className={`py-2 text-xs font-bold rounded border text-center transition-colors ${currentLang === 'ar' ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-transparent shadow-sm' : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  عربي
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  );
};
